import { DestroyRef, inject, Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';

import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { UserService } from 'src/app/services/user/user.service';
import { NAV_SECTIONS, NavSection } from 'src/app/services/sidebar-nav/nav-section';
import { OwnChatsRegistry } from './own-chats.registry';

/** Per section, when the user last opened it (epoch ms). */
export type NavSeenMarks = Record<NavSection, number>;

/** Per section, the creation times (epoch ms) of the items the rail already holds in memory. */
export type NavSectionCreations = Record<NavSection, readonly number[]>;

/** A clear with nothing to clear re-writes at most this often (spec §3.2 throttle). */
export const NAV_SEEN_IDLE_WRITE_MS = 10 * 60 * 1000;

const STORAGE_PREFIX = 'crwd.nav.seen.';

/**
 * "New items" badges on the four sidebar sections (onboarding/navigation spec
 * §3.2). New = created since the user last opened that section, per org.
 *
 * - Counts come from the lists the rail already listens to — this service
 *   opens NO Firestore listener and does no read of its own.
 * - The marks live on the user doc, `users/{uid}.navSeen.{orgId}.{section}`,
 *   read through UserService's existing user-doc listener (`navSeen$`) and
 *   written with a dot-path `docUpdate` (owner-only rule, no rules change).
 *   They are mirrored to localStorage for first paint.
 * - First run for an org (no marks anywhere): all four start at now, so an
 *   existing user is not flooded with badges for everything they already have.
 *   A section missing from a partial record starts at now the same way.
 */
@Injectable({ providedIn: 'root' })
export class NavSeenService {
  private readonly firebase = inject(FirebaseService);
  private readonly user = inject(UserService);
  /** Chats created in this browser session — the user made them, so they are not news. */
  private readonly ownChats = inject(OwnChatsRegistry);

  /** Fires when marks change (remote echo, another device, a local clear). */
  public readonly changes$ = new Subject<void>();

  /** Marks per org: the newest of the user doc, localStorage and local clears. */
  private readonly marks = new Map<string, NavSeenMarks>();
  /** True once the user doc has delivered (until then a missing org is "unknown", not "first run"). */
  private remoteLoaded = false;
  /** Last write per `${orgId}:${section}`, for the idle throttle. */
  private readonly lastWrite = new Map<string, number>();
  private readonly subscription: Subscription;

  constructor() {
    this.subscription = this.user.navSeen$.subscribe((remote) => {
      if (remote === null) return;
      this.remoteLoaded = true;
      for (const [orgId, value] of Object.entries(remote)) {
        this.merge(orgId, value);
      }
      this.changes$.next();
    });
    inject(DestroyRef).onDestroy(() => this.subscription.unsubscribe());
  }

  /** Records a chat the user just created here (the composer), so it never badges Chats. */
  public recordOwnChat(chatId: string): void {
    this.ownChats.record(chatId);
  }

  public isOwnChat(chatId: string): boolean {
    return this.ownChats.has(chatId);
  }

  /**
   * The marks for an org, or null while they are unknown (no localStorage copy
   * and the user doc not loaded yet) — the caller shows no badge then.
   */
  public marksFor(orgId: string | null | undefined, now = Date.now()): NavSeenMarks | null {
    if (!orgId) return null;
    const known = this.marks.get(orgId) ?? this.readLocal(orgId, now);
    if (known) {
      this.marks.set(orgId, known);
      return known;
    }
    if (!this.remoteLoaded) return null;
    // First run for this org: start every section at now, without badges.
    const fresh = NAV_SECTIONS.reduce((acc, section) => ({ ...acc, [section]: now }), {} as NavSeenMarks);
    this.marks.set(orgId, fresh);
    this.writeLocal(orgId, fresh);
    this.writeRemote(orgId, fresh, now);
    return fresh;
  }

  /** Items created after the section's mark, per section (0 while the marks are unknown). */
  public counts(orgId: string | null | undefined, creations: NavSectionCreations, now = Date.now()): Record<NavSection, number> {
    const marks = this.marksFor(orgId, now);
    return NAV_SECTIONS.reduce((acc, section) => ({
      ...acc,
      [section]: marks ? creations[section].filter((ms) => ms > marks[section]).length : 0,
    }), {} as Record<NavSection, number>);
  }

  /**
   * The user is in `section`: move its mark to `max(now, newest item)` (the max
   * absorbs clock skew between this device and the server timestamps).
   * Written to the user doc only when the section had a badge, or the last
   * write is older than {@link NAV_SEEN_IDLE_WRITE_MS} — never per navigation.
   * Returns true when the user doc was written.
   */
  public markSeen(orgId: string | null | undefined, section: NavSection, creations: readonly number[], hadBadge: boolean, now = Date.now()): boolean {
    const marks = this.marksFor(orgId, now);
    if (!orgId || !marks) return false;
    // A mark is itself the time of its last write, so a reload does not re-write a fresh one.
    const last = Math.max(this.lastWrite.get(`${orgId}:${section}`) ?? 0, marks[section]);
    if (!hadBadge && now - last < NAV_SEEN_IDLE_WRITE_MS) return false;
    const value = Math.max(now, ...creations);
    const next = { ...marks, [section]: Math.max(marks[section], value) };
    this.marks.set(orgId, next);
    this.writeLocal(orgId, next);
    this.writeRemote(orgId, { [section]: next[section] }, now);
    return true;
  }

  /** 1–9, or '9+' past nine; undefined for none — the kit's badge slot. */
  public static badge(count: number): number | '9+' | undefined {
    if (!count || count < 1) return undefined;
    return count > 9 ? '9+' : count;
  }

  // ── storage ──

  private merge(orgId: string, value: unknown, now = Date.now()): void {
    const incoming = this.sanitize(value);
    if (!incoming) return;
    const current = this.marks.get(orgId) ?? this.readLocal(orgId, now);
    // Marks only move forward: a stale echo never resurrects a cleared badge.
    const merged: Partial<NavSeenMarks> = {};
    for (const section of NAV_SECTIONS) {
      const ms = Math.max(current?.[section] ?? 0, incoming[section] ?? 0);
      if (ms > 0) merged[section] = ms;
    }
    const { marks, filled } = this.complete(merged, now);
    this.marks.set(orgId, marks);
    this.writeLocal(orgId, marks);
    // Store the filled-in sections too, so every device agrees on them.
    if (filled.length) {
      this.writeRemote(orgId, Object.fromEntries(filled.map((section) => [section, marks[section]])), now);
    }
  }

  /**
   * A section missing from a partial record (an older client, an interrupted
   * write) is unknown, not "never opened": it starts at now, like a first run.
   * Reading it as 0 badged every item the user already had (review S6).
   */
  private complete(partial: Partial<NavSeenMarks>, now: number): { marks: NavSeenMarks; filled: NavSection[] } {
    const filled = NAV_SECTIONS.filter((section) => !partial[section]);
    const marks = NAV_SECTIONS.reduce((acc, section) => ({ ...acc, [section]: partial[section] || now }), {} as NavSeenMarks);
    return { marks, filled };
  }

  /** The valid sections of a stored record (null when none is valid); {@link complete} fills the rest. */
  private sanitize(value: unknown): Partial<NavSeenMarks> | null {
    if (!value || typeof value !== 'object') return null;
    const out: Partial<NavSeenMarks> = {};
    for (const section of NAV_SECTIONS) {
      const ms = Number((value as Record<string, unknown>)[section]);
      if (Number.isFinite(ms) && ms > 0) out[section] = ms;
    }
    return Object.keys(out).length ? out : null;
  }

  private storageKey(orgId: string): string {
    return `${STORAGE_PREFIX}${this.user.user?.uid ?? 'anon'}.${orgId}`;
  }

  private readLocal(orgId: string, now = Date.now()): NavSeenMarks | null {
    try {
      const raw = localStorage.getItem(this.storageKey(orgId));
      const parsed = raw ? this.sanitize(JSON.parse(raw)) : null;
      return parsed ? this.complete(parsed, now).marks : null;
    } catch {
      return null;
    }
  }

  private writeLocal(orgId: string, marks: NavSeenMarks): void {
    try {
      localStorage.setItem(this.storageKey(orgId), JSON.stringify(marks));
    } catch {
      // Private mode / quota: the user doc still carries the marks.
    }
  }

  private writeRemote(orgId: string, marks: Partial<NavSeenMarks>, now: number): void {
    const uid = this.user.user?.uid;
    if (!uid) return;
    const update: Record<string, number> = {};
    for (const [section, ms] of Object.entries(marks)) {
      update[`navSeen.${orgId}.${section}`] = ms as number;
      this.lastWrite.set(`${orgId}:${section}`, now);
    }
    this.firebase.globalFirestore.docUpdate(`users/${uid}`, update)
      .catch((err) => console.warn('[NavSeen] Could not save the sidebar marks', err));
  }
}
